import {
  Button,
  Card,
  FileText,
  StatusDropdown,
  XCircle,
} from "../../../../ui";
import {
  DetailSection,
  FieldsGrid,
  Field,
  FileItem,
  StageCard,
} from "../Shared/Shared";
import StoreReview from "../StoreReview/StoreReview";
import { formatDate } from "../../../onboarding";

// Declared at module level: defining it inside DetailView made React treat it
// as a new component type on every render and remount its whole subtree.
const Section = ({ title, children }) => (
  <div className="expand-section">
    <h4>{title}</h4>
    <div className="expand-flex">{children}</div>
  </div>
);

export default function DetailView({
  data,
  activeStage,
  setActiveStage,
  onClose,
  lang,
  isArabic,
  t,
}) {
  if (!data) return null;

  const renderWorkflow = (data) => (
    <DetailSection title={t.workflow}>
      <FieldsGrid>
        <Field label={t.currentStage} value={data.workFlow?.currentStage} />
        <Field
          label={t.requestStatus}
          value={t[data.workFlow?.status] || data.workFlow?.status}
        />
        <Field
          label={t.submittedAt}
          value={formatDate(data.workFlow?.submittedAt, lang)}
        />
        <Field
          label={t.approvedAt}
          value={formatDate(data.workFlow?.approvedAt, lang)}
        />
        <Field
          label={t.rejectionReason}
          value={data.workFlow?.rejectionReason}
        />
      </FieldsGrid>

      {!!data.workFlow?.history?.length && (
        <div className="admin-onboarding-history">
          <div className="admin-onboarding-detail-title small">{t.history}</div>
          <div className="admin-onboarding-history-table">
            <div className="admin-onboarding-history-row admin-onboarding-history-head">
              <span>{t.currentStage}</span>
              <span>{t.requestStatus}</span>
              <span>{t.created}</span>
              <span>{t.rejectionReason}</span>
            </div>

            {data.workFlow.history.map((item, index) => (
              <div className="admin-onboarding-history-row" key={index}>
                <span>{item.stage || "-"}</span>
                <span>{t[item.action] || item.action || "-"}</span>
                <span>{formatDate(item.createdAt, lang)}</span>
                <span>{item.reason || "-"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailSection>
  );

  const renderStage1 = (data) => (
    <div className="admin-onboarding-stage-content">
      <div className="expand-grid">
        <Section title="Customer Overview">
          <Field label="Business name" value={data?.store?.businessName} />
          <Field label="City" value={data?.store?.city} />
          <Field label="Branch count" value={data?.store?.branchCount} />
          <Field label="Owner Name" value={data?.store?.ownerName} />
          <Field label="Owner Contact" value={data?.store?.ownerNumber} />
          <Field label="Owner Email" value={data?.store?.ownerEmail} />
        </Section>
        <Section title="Contact Information">
          <Field label="Contact name" value={data?.contact?.contactName} />
          <Field label="Job title" value={data?.contact?.jobTitle} />
          <Field label="Mobile" value={data?.contact?.mobile} />
          <Field
            label="Email"
            value={data?.contact?.email || data?.store?.email}
          />
        </Section>
        <Section title="Business / Entity Information">
          <Field label="Entity" value={data?.entity?.entityName} />
          <Field
            label="CR number"
            value={data?.entity?.commercialRegistration}
          />
          <Field
            label="CR expiration"
            value={
              data?.entity?.crExpiration
                ? new Date(data?.entity?.crExpiration).toLocaleDateString()
                : "-"
            }
          />
          <Field label="VAT number" value={data?.entity?.vatNumber} />
        </Section>
        <Section title="Documents">
          {renderDocuments(data, ["cr", "vat"])}
        </Section>
      </div>
    </div>
  );

  const renderStage2 = (data) => (
    <div className="admin-onboarding-stage-content">
      {renderQuotation(data)}
    </div>
  );

  const renderDocuments = (data, categories) => {
    const files = data.documents?.files || [];
    const filteredFiles = files
      .map((file, originalIndex) => ({ file, originalIndex }))
      .filter(({ file }) =>
        categories?.length ? categories.includes(file.category) : true,
      );

    return (
      <DetailSection title={t.documents}>
        {filteredFiles.length ? (
          <div className="admin-onboarding-files-grid">
            {filteredFiles.map(({ file, originalIndex }) => (
              <FileItem
                key={`${file.category}-${originalIndex}`}
                label={file.category || t.file}
                file={file}
                onboardingId={data._id}
                kind="document"
                index={originalIndex}
              />
            ))}
          </div>
        ) : (
          <div className="admin-onboarding-empty-card">
            <FileText size={20} />
            <span>{t.noData}</span>
          </div>
        )}

        <Field
          label={isArabic ? "ملاحظات المستندات" : "Document Notes"}
          value={data.documents?.notes}
        />
      </DetailSection>
    );
  };

  const renderQuotation = (data) => {
    const quote = data.quotation;

    const ludPrice = Number(quote?.ludPrice || 500);
    const addonPrice = Number(quote?.addonPrice || 0);
    const discountAmount = Number(quote?.discountAmount || 0);
    const totalBeforeDiscount = ludPrice + addonPrice;
    const finalPrice = Number(
      quote?.finalPrice ?? Math.max(totalBeforeDiscount - discountAmount, 0),
    );

    return (
      <DetailSection title={t.quotation}>
        <div className="admin-onboarding-quote-preview">
          <div className="admin-onboarding-quote-head">
            <div>
              <span>{t.quotation}</span>
              <strong>
                {isArabic
                  ? "تحليل أداء وهيكلة قائمة طعام احترافية وصور لتطبيقات التوصيل"
                  : "Performance Analysis, Menu Restructuring, and Delivery App Photos"}
              </strong>
            </div>
            <div>
              <small>{isArabic ? "الإجمالي" : "Total"}</small>
              <b>
                {finalPrice.toLocaleString()} {isArabic ? "ريال" : "SAR"}
              </b>
            </div>
          </div>

          <div className="admin-onboarding-quote-table">
            <div className="admin-onboarding-quote-row head">
              <span>{isArabic ? "الخدمة" : "Service"}</span>
              <span>{isArabic ? "الوصف" : "Description"}</span>
              <span>{isArabic ? "السعر" : "Price"}</span>
            </div>
            <div className="admin-onboarding-quote-row">
              <span>
                {isArabic
                  ? "تحليل أداء وهيكلة قائمة طعام"
                  : "Performance analysis and menu restructuring"}
              </span>
              <span>
                {isArabic
                  ? "تحليل شامل للتقارير، كشف الأخطاء، تحليل الأداء، ومراجعة قائمة الطعام."
                  : "Full reports analysis, error detection, performance review, and menu review."}
              </span>
              <span>
                {ludPrice.toLocaleString()} {isArabic ? "ريال" : "SAR"}
              </span>
            </div>
            {quote?.addonAiPhotosEnabled && (
              <div className="admin-onboarding-quote-row">
                <span>
                  {isArabic
                    ? "صور AI مخصصة لتطبيقات التوصيل"
                    : "AI delivery-app photos"}
                </span>
                <span>
                  {(quote?.addonAiPhotosCount || 10).toLocaleString()}{" "}
                  {isArabic
                    ? "صورة احترافية + بنر مجاناً"
                    : "professional photos + free banner"}
                </span>
                <span>
                  {addonPrice.toLocaleString()} {isArabic ? "ريال" : "SAR"}
                </span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="admin-onboarding-quote-row">
                <span>{isArabic ? "خصم" : "Discount"}</span>
                <span>
                  {isArabic
                    ? "خصم على السعر النهائي"
                    : "Discount on final price"}
                </span>
                <span>
                  {discountAmount.toLocaleString()} {isArabic ? "ريال" : "SAR"}
                </span>
              </div>
            )}
          </div>
        </div>

        <FieldsGrid>
          <Field label={t.discountCode} value={quote?.discountCode} />
          <Field label={t.discountPercent} value={quote?.discountPercent} />
          <Field label={t.discountAmount} value={discountAmount} />
          <Field label={t.finalPrice} value={finalPrice} />
        </FieldsGrid>

        <FileItem
          label={t.receipt}
          file={quote?.paymentReceipt}
          onboardingId={data._id}
          kind="quotation"
        />
      </DetailSection>
    );
  };

  return (
    <>
      <Card
        variant="guideline"
        padding="lg"
        className="admin-onboarding-detail"
      >
        <div className="admin-onboarding-detail-header">
          <div>
            <h3>{data.store?.businessName || t.notSelected}</h3>
            <p>{data.store?.city}</p>
              <StatusDropdown
                editable={false}
                value={t[data?.workFlow?.status] || "active"}
                getLabel={() => t[data?.workFlow?.status]}
              />
          </div>

          <div className="admin-onboarding-detail-actions">
            <Button
              variant="primary"
              appearance="soft"
              className="admin-onboarding-close-btn"
              icon={<XCircle size={18} />}
              title={t.close}
              aria-label={t.close}
              onClick={onClose}
            />
          </div>
        </div>

        <div className="admin-onboarding-stage-tabs">
          <StageCard
            title={t.stage1}
            description={t.stage1Desc}
            active={activeStage === 1}
            onClick={() => setActiveStage(1)}
          />
          <StageCard
            title={t.stage2}
            description={t.stage2Desc}
            active={activeStage === 2}
            onClick={() => setActiveStage(2)}
          />
          <StageCard
            title={t.workflow}
            description={t.workflowDesc}
            active={activeStage === "workflow"}
            onClick={() => setActiveStage("workflow")}
          />
        </div>

        {activeStage === 1 && renderStage1(data)}
        {activeStage === 2 && renderStage2(data)}
        {data.storeOnboarding?.linked && (
          <StoreReview key={data.storeOnboarding.storeCode} storeCode={data.storeOnboarding.storeCode} lang={lang} />
        )}
        {activeStage === "workflow" && renderWorkflow(data)}
        {/* Phase 4: the quotation / payment / approval workflow of this historical record is retired. Information is reviewed in the
            store panel above, payments in Billing. The record stays readable. */}
        <div className="admin-onboarding-actions">
          <p className="admin-onboarding-retired" role="note">
            {isArabic
              ? "هذا سجل تاريخي للنظام القديم (للقراءة فقط). راجع بيانات المتجر أعلاه، وراجع المدفوعات من صفحة الفواتير والاشتراكات."
              : "This is a historical record of the old workflow (read-only). Review the store information above and review payments in Billing."}
          </p>
        </div>
      </Card>
    </>
  );
}
